import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import PlayAnimation from '@/components/PlayAnimation';
import { api } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { ChevronLeft, Brain, Loader, AlertTriangle, Lightbulb, Target, Zap } from 'lucide-react';

const DEFENSES = ['man', 'zone 2-3', 'zone 3-2', 'zone 1-3-1', 'switch', 'press'];
const SKILLS = [
  { id: 'beginner', key: 'beginner' },
  { id: 'intermediate', key: 'intermediate' },
  { id: 'advanced', key: 'advanced' },
];

export default function Simulator() {
  const { playId } = useParams();
  const { t, lang } = useLang();
  const [play, setPlay] = useState(null);
  const [defense, setDefense] = useState('man');
  const [skill, setSkill] = useState('intermediate');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!playId) return;
    api.get(`/plays/${playId}`).then(r => setPlay(r.data));
  }, [playId]);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post(`/simulator/${playId}`, { defense, skill_level: skill, language: lang });
      setResult(r.data.result);
    } catch (e) {
      // swallow
    } finally {
      setLoading(false);
    }
  };

  if (!play) return <Layout><div className="text-gray-400">{t('loading')}</div></Layout>;

  return (
    <Layout>
      <Link to={`/playbook/${playId}`} className="inline-flex items-center gap-1 text-gold hover:underline mb-4 text-sm uppercase tracking-wider font-h">
        <ChevronLeft className="w-4 h-4" /> {t('sim_back_to_play')}
      </Link>

      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('sim_eyebrow')}</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl flex items-center gap-3">
          <Brain className="w-9 h-9 text-gold" /> {play.name}
        </h1>
        <p className="text-gray-400 mt-2">{t('sim_subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <PlayAnimation play={play} />
        </div>

        <div className="space-y-4">
          <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2">{t('sim_rival_defense')}</div>
            <div className="flex flex-wrap gap-1.5">
              {DEFENSES.map(d => (
                <button key={d} onClick={() => setDefense(d)} data-testid={`sim-def-${d}`}
                  className={`px-3 py-1.5 rounded text-xs font-h uppercase tracking-wider border ${defense === d ? 'bg-gold text-navy border-gold' : 'border-steel text-gray-300 hover:border-gold/60'}`}
                >{d}</button>
              ))}
            </div>

            <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2 mt-5">{t('sim_exec_level')}</div>
            <div className="flex gap-1.5">
              {SKILLS.map(s => (
                <button key={s.id} onClick={() => setSkill(s.id)} data-testid={`sim-skill-${s.id}`}
                  className={`flex-1 px-3 py-2 rounded text-xs font-h uppercase tracking-wider border ${skill === s.id ? 'bg-gold text-navy border-gold' : 'border-steel text-gray-300 hover:border-gold/60'}`}
                >{t(s.key)}</button>
              ))}
            </div>

            <button data-testid="sim-run-btn" onClick={run} disabled={loading}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-bold hover:bg-gold-light disabled:opacity-50 animate-glow-pulse">
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {loading ? t('sim_running') : t('sim_run_now')}
            </button>
          </div>

          {result && (
            <div className="space-y-3 animate-fade-in-up">
              <div className="bg-gold/10 border-2 border-gold rounded-lg p-5">
                <div className="text-xs font-mono uppercase tracking-widest text-gold mb-1">{t('sim_ai_prediction')}</div>
                <div className="font-h text-5xl text-gold">{result.predicted_success_pct}%</div>
                <div className="text-xs uppercase tracking-widest text-gold/80 mt-1">{t('sim_confidence')} {result.confidence}</div>
              </div>

              <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
                <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2 flex items-center gap-1"><Target className="w-3 h-3" /> {t('sim_def_reaction')}</div>
                <p className="text-gray-100 text-sm leading-relaxed">{result.defense_reaction}</p>
              </div>

              <div className="bg-card-steel border border-alert/40 rounded-lg p-5">
                <div className="text-xs font-mono uppercase tracking-widest text-alert mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {t('sim_failure_points')}</div>
                <ul className="space-y-1 text-sm">
                  {(result.failure_points || []).map((f, i) => (
                    <li key={i} className="flex gap-2 text-gray-200"><span className="text-alert">•</span> {f}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-card-steel border border-green-500/40 rounded-lg p-5">
                <div className="text-xs font-mono uppercase tracking-widest text-green-400 mb-2 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> {t('sim_adjustments')}</div>
                <ul className="space-y-1 text-sm">
                  {(result.adjustments || []).map((a, i) => (
                    <li key={i} className="flex gap-2 text-gray-200"><span className="text-green-400">✓</span> {a}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-navy-deep border border-gold/30 rounded-lg p-4 text-sm">
                <span className="text-gold uppercase tracking-widest text-xs me-2">{t('sim_key_player')}</span>
                <span className="text-gray-200">{result.key_player_action}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
